/**
 * Environments API Service
 * Manages student development environments
 */

import { environmentOrchestratorApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import type { Environment, EnvironmentStats, PaginatedResponse } from '../types';

interface EnvironmentListParams {
  page?: number;
  limit?: number;
  status?: 'running' | 'stopped' | 'error';
  labId?: string;
  userId?: string;
}

/**
 * Get all environments
 */
export async function getEnvironments(params: EnvironmentListParams = {}): Promise<PaginatedResponse<Environment>> {
  try {
    const response = await environmentOrchestratorApi.get('/admin/environments', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch environments:', error);
    // Return mock data for development
    const mockEnvironments: Environment[] = [
      {
        id: 'env-1',
        userId: 'u1',
        studentName: 'John Doe',
        studentEmail: 'john@example.com',
        labId: 'lab-1',
        labTitle: 'RAG Fundamentals',
        status: 'running',
        podName: 'env-u1-lab1-abc123',
        containerImage: 'ai-training/python-ml:latest',
        resourceUsage: {
          cpuPercent: 45,
          cpuLimit: '2',
          memoryMB: 1200,
          memoryLimitMB: 4096,
        },
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        lastActivityAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
        uptime: '2h 15m',
      },
      {
        id: 'env-2',
        userId: 'u2',
        studentName: 'Jane Smith',
        studentEmail: 'jane@example.com',
        labId: 'lab-2',
        labTitle: 'OpenAI API Intro',
        status: 'running',
        podName: 'env-u2-lab2-def456',
        containerImage: 'ai-training/python-ml:latest',
        resourceUsage: {
          cpuPercent: 12,
          cpuLimit: '2',
          memoryMB: 800,
          memoryLimitMB: 4096,
        },
        createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        lastActivityAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
        uptime: '45m',
      },
      {
        id: 'env-3',
        userId: 'u3',
        studentName: 'Bob Johnson',
        studentEmail: 'bob@example.com',
        labId: 'lab-3',
        labTitle: 'Fine-tuning 101',
        status: 'stopped',
        containerImage: 'ai-training/pytorch:latest',
        resourceUsage: {
          cpuPercent: 0,
          cpuLimit: '4',
          memoryMB: 0,
          memoryLimitMB: 8192,
        },
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        lastActivityAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
        uptime: '-',
      },
      {
        id: 'env-4',
        userId: 'u4',
        studentName: 'Alice Brown',
        studentEmail: 'alice@example.com',
        labId: 'lab-4',
        labTitle: 'LangChain Basics',
        status: 'error',
        containerImage: 'ai-training/python-ml:latest',
        resourceUsage: {
          cpuPercent: 0,
          cpuLimit: '2',
          memoryMB: 0,
          memoryLimitMB: 4096,
        },
        createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        lastActivityAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        uptime: '-',
      },
      {
        id: 'env-5',
        userId: 'u5',
        studentName: 'Charlie Wilson',
        studentEmail: 'charlie@example.com',
        labId: 'lab-1',
        labTitle: 'RAG Fundamentals',
        status: 'running',
        podName: 'env-u5-lab1-ghi789',
        containerImage: 'ai-training/python-ml:latest',
        resourceUsage: {
          cpuPercent: 78,
          cpuLimit: '2',
          memoryMB: 3200,
          memoryLimitMB: 4096,
        },
        createdAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        lastActivityAt: new Date(Date.now() - 1000 * 60 * 1).toISOString(),
        uptime: '1h 30m',
      },
    ];

    let filtered = mockEnvironments;
    if (params.status) {
      filtered = filtered.filter((e) => e.status === params.status);
    }
    if (params.labId) {
      filtered = filtered.filter((e) => e.labId === params.labId);
    }
    if (params.userId) {
      filtered = filtered.filter((e) => e.userId === params.userId);
    }

    const mock = {
      data: filtered,
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: filtered.length,
        totalPages: Math.ceil(filtered.length / (params.limit || 20)),
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get environment statistics
 */
export async function getEnvironmentStats(): Promise<EnvironmentStats> {
  try {
    const response = await environmentOrchestratorApi.get('/resources/usage');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch environment stats:', error);
    const mock: EnvironmentStats = {
      total: 89,
      running: 15,
      stopped: 70,
      error: 4,
      totalCpuPercent: 135,
      totalMemoryMB: 5200,
      totalMemoryLimitMB: 20480,
    };
    return mockFallback(error, mock);
  }
}

/**
 * Stop an environment
 */
export async function stopEnvironment(userId: string, environmentId: string): Promise<void> {
  await environmentOrchestratorApi.post(`/environments/${userId}/${environmentId}/stop`);
}

/**
 * Start an environment
 */
export async function startEnvironment(userId: string, environmentId: string): Promise<void> {
  await environmentOrchestratorApi.post(`/environments/${userId}/${environmentId}/start`);
}

/**
 * Restart an environment
 */
export async function restartEnvironment(userId: string, environmentId: string): Promise<void> {
  await environmentOrchestratorApi.post(`/environments/${userId}/${environmentId}/restart`);
}

/**
 * Delete an environment
 */
export async function deleteEnvironment(userId: string, environmentId: string): Promise<void> {
  await environmentOrchestratorApi.delete(`/environments/${userId}/${environmentId}`);
}

/**
 * Get environment logs
 */
export async function getEnvironmentLogs(userId: string, environmentId: string): Promise<string> {
  try {
    const response = await environmentOrchestratorApi.get(`/environments/${userId}/${environmentId}/logs`);
    return response.data.logs;
  } catch (error) {
    console.error('Failed to fetch logs:', error);
    return mockFallback(error, 'Failed to fetch logs');
  }
}

/**
 * Connect to environment terminal (returns WebSocket URL)
 */
export function getTerminalUrl(userId: string, environmentId: string): string {
  const baseUrl = process.env.REACT_APP_WEBSOCKET_GATEWAY_URL || 'http://localhost:3005';
  return `${baseUrl.replace('http', 'ws')}/terminal/${userId}/${environmentId}`;
}
